import { useState, useEffect } from "react";
import { X, ExternalLink, Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { ProfileHero } from "@/components/teacher/profile/ProfileHero";
import { AboutSection } from "@/components/teacher/profile/AboutSection";
import { IntroVideo } from "@/components/teacher/profile/IntroVideo";
import { OfferingsList } from "@/components/teacher/profile/OfferingsList";
import { SubjectExpertise } from "@/components/teacher/profile/SubjectExpertise";
import { ContactCard } from "@/components/teacher/profile/BookingCard";
import { InquiryComposerModal } from "@/components/InquiryComposerModal";
import { savePendingIntent } from "@/utils/pendingIntent";
import { useAuth } from "@/hooks/use-auth-compat";
import { useMyInquiries } from "@/hooks/use-inquiries";
import { OPEN_THREAD_STATUSES } from "@/services/inquiryService";
import { getProfile } from "@/services/UserManagmentServices";
import {
  getTeacherByHandle,
  getTeacherOfferings,
  type PublicTeacherProfile,
  type PublicOffering,
} from "@/services/marketplaceService";

function computeMinPrice(offerings: PublicOffering[]): { minPrice: number; currency: string } | null {
  const allPrices: { price: number; currency: string }[] = offerings.flatMap(
    o => (o.pricingPlans ?? []).map(p => ({ price: p.perSessionEquivalent ?? p.regularPrice, currency: p.currency }))
  );
  if (allPrices.length === 0) return null;
  const min = allPrices.reduce((a, b) => (b.price < a.price ? b : a));
  return { minPrice: min.price, currency: min.currency };
}

interface TeacherProfileSheetProps {
  /** The teacher handle to load, or null to close the sheet */
  handle: string | null;
  onClose: () => void;
}

export function TeacherProfileSheet({ handle, onClose }: TeacherProfileSheetProps) {
  const navigate = useNavigate();
  const auth = useAuth();
  const { data: myInquiries } = useMyInquiries(0, 50);

  const [profile, setProfile] = useState<PublicTeacherProfile | null>(null);
  const [offerings, setOfferings] = useState<PublicOffering[]>([]);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [selectedOfferingId, setSelectedOfferingId] = useState<string | undefined>(undefined);
  const [goalOnboardingCompleted, setGoalOnboardingCompleted] = useState<boolean | null>(null);

  const existingThreadId = profile?.teacherUserId
    ? (myInquiries?.content ?? []).find(
        (t) => t.teacherUserId === profile.teacherUserId && OPEN_THREAD_STATUSES.has(t.status)
      )?.id
    : undefined;

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    (async () => {
      const token = await auth.getToken();
      if (!token) return;
      getProfile(token)
        .then(p => setGoalOnboardingCompleted(p.goalOnboardingCompleted))
        .catch(() => setGoalOnboardingCompleted(null));
    })();
  }, [auth.isAuthenticated, auth.getToken]);

  useEffect(() => {
    if (!handle) {
      setProfile(null);
      setOfferings([]);
      setNotFound(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    setProfile(null);
    setOfferings([]);

    Promise.allSettled([
      getTeacherByHandle(handle),
      getTeacherOfferings(handle),
    ]).then(([profileResult, offeringsResult]) => {
      if (profileResult.status === "rejected" || !profileResult.value) {
        setNotFound(true);
      } else {
        setProfile(profileResult.value);
      }
      if (offeringsResult.status === "fulfilled") {
        setOfferings(offeringsResult.value);
      }
    }).finally(() => setLoading(false));
  }, [handle]);

  const handleContact = (offeringId?: string) => {
    if (!auth.isAuthenticated) {
      const raw = sessionStorage.getItem("bmc:search-context");
      const searchCtx = raw ? JSON.parse(raw) : null;
      localStorage.setItem("bmc:anonymous-search-context", JSON.stringify({
        teacherHandle: handle,
        intendedOfferingId: offeringId ?? null,
        searchQuery: searchCtx?.searchQuery ?? null,
        activeFilters: searchCtx?.activeFilters ?? null,
      }));
      window.dispatchEvent(new CustomEvent("bmc:open-registration-gate", { detail: { source: "teacher-profile" } }));
      return;
    }
    if (goalOnboardingCompleted === false) {
      if (handle) savePendingIntent({ type: 'INQUIRY', teacherHandle: handle });
      navigate('/goal-onboarding');
      return;
    }
    setSelectedOfferingId(offeringId);
    setInquiryOpen(true);
  };

  const priceInfo = profile ? computeMinPrice(offerings) : null;
  const trialAvailable = offerings.some(o => o.trialSessionAvailable);

  return (
    <>
      <Sheet open={!!handle} onOpenChange={(open) => { if (!open) onClose(); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl p-0 flex flex-col overflow-hidden gap-0"
        >
          <SheetTitle className="sr-only">{profile?.fullName ?? "Teacher Profile"}</SheetTitle>

          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
              Close
            </button>
            {handle && (
              <Link
                to={`/t/${handle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
              >
                View full profile
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {!loading && notFound && (
              <div className="flex items-center justify-center h-64 text-center px-6">
                <div>
                  <p className="text-base font-medium mb-1">Profile not found</p>
                  <p className="text-sm text-muted-foreground">This teacher's profile is not available.</p>
                </div>
              </div>
            )}

            {!loading && profile && (
              <div>
                <div className="px-5 pt-5 pb-4">
                  <ProfileHero
                    fullName={profile.fullName}
                    handle={profile.handle ?? handle ?? ""}
                    profileImage={profile.profileImage}
                    subjects={profile.subjects ?? []}
                    locationDisplayName={profile.locationDisplayName}
                    city={profile.city}
                    country={profile.country}
                    yearsTeachingExperience={profile.yearsTeachingExperience}
                    highestQualification={profile.highestQualification}
                    overallStatus={profile.overallStatus}
                    offeringsCount={offerings.length}
                    minPrice={priceInfo?.minPrice}
                    minPriceCurrency={priceInfo?.currency}
                    teachingModes={profile.teachingModes}
                    instructionLanguages={profile.instructionLanguages}
                  />
                </div>

                <div className="px-5 pb-5">
                  <ContactCard
                    fullName={profile.fullName}
                    handle={profile.handle ?? handle ?? ""}
                    trialAvailable={trialAvailable}
                    offeringsCount={offerings.length}
                    minPrice={priceInfo?.minPrice}
                    minPriceCurrency={priceInfo?.currency}
                    onAuthenticatedContact={() => handleContact()}
                    existingThreadId={existingThreadId}
                  />
                </div>

                {(profile.subjectExpertise ?? []).length > 0 && (
                  <div className="border-t border-border px-5 py-5">
                    <SubjectExpertise subjectExpertise={profile.subjectExpertise} />
                  </div>
                )}

                {profile.professionalBio && (
                  <div className="border-t border-border px-5 py-5">
                    <AboutSection professionalBio={profile.professionalBio} />
                  </div>
                )}

                {profile.introVideo && (
                  <div className="border-t border-border px-5 py-5">
                    <IntroVideo introVideo={profile.introVideo} />
                  </div>
                )}

                {offerings.length > 0 && (
                  <div className="border-t border-border px-5 py-5">
                    <OfferingsList
                      offerings={offerings}
                      teacherHandle={profile.handle ?? handle ?? ""}
                      onContactOffering={(offeringId) => handleContact(offeringId)}
                    />
                  </div>
                )}

                <div className="h-8" />
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {inquiryOpen && profile && (
        <InquiryComposerModal
          open={inquiryOpen}
          onClose={() => { setInquiryOpen(false); setSelectedOfferingId(undefined); }}
          teacherUserId={profile.teacherUserId ?? ""}
          teacherName={profile.fullName}
          teacherHandle={profile.handle ?? handle ?? ""}
          offeringId={selectedOfferingId}
        />
      )}
    </>
  );
}
